import React, { useEffect } from 'react';
import styled, { css } from 'styled-components';
import { observer } from 'mobx-react-lite';
import { Plus } from 'styled-icons/bootstrap';
import mergeClassNames from '../../../lib/style-helpers/merge-class-name';
import Button, { ButtonType } from '../../button/button';
import HiddenFileInput from '../../hidden-file-input/hidden-file-input';
import { FileUploadViewProps } from '../file-upload-provider';
import DropArea from '../../drop/drop-area';
import { FontSize, FontType } from '../../../../../config/theme/types/themeTypes';
import Icon from '../../icons/icon';
import { IconType } from '../../icons/icon-type';
import AppConfig from '../../../../../config/app-config';
import useNotifications from '../../../../../hooks/useNotifications/useNotifications';
import FileCardFancy from './file-card-fancy';
import FileCard from './file-card';

function UploadButton({ onClick }: { onClick: (e: React.MouseEvent) => void }) {
  return (
    <LargeUploadButton
      onClick={onClick}
      type={ButtonType.Form}
    >
      <PlusIcon />
      {AppConfig.Copy.UploadPage.addButton.label}
    </LargeUploadButton>
  );
}

function FileUploadView({
  viewModel,
  className,
  renderChildren,
  dropEvents,
  ...props
}: React.PropsWithRef<FileUploadViewProps>): JSX.Element {
  const {
    tokens, isDragging, uploadRef, showUploadDialog, handleUploadBtn,
  } = viewModel;

  const isLower = AppConfig.themeName.toLowerCase() === 'lower' || AppConfig.themeName.toLowerCase() === 'lwr';

  const isOpendoor = AppConfig.themeName.toLowerCase() === 'opendoor';

  const { clearNotifications } = useNotifications();

  useEffect(() => {
    clearNotifications();
  }, [clearNotifications]);

  return (
    <DropArea {...dropEvents}>
      <Container
        className={mergeClassNames('FileUploadView', isDragging && '--dragging', className)}
        {...props}
      >
        <FormContainer>
          <UploadButton onClick={showUploadDialog} />
          {
            !!renderChildren && renderChildren(viewModel)
          }

          <FilesContainer>
            {
              tokens && tokens.map(
                (token) => (
                  isLower || isOpendoor
                    ? (
                      <FileCardFancy
                        key={token.id}
                        {...token}
                      />
                    )
                    : (
                      <FileCard
                        key={token.id}
                        {...token}
                      />
                    )
                ),
              )
            }
            <DragFeedback className={mergeClassNames('DragFeedback', isDragging && '--dragging')}>
              <Icon type={IconType.Upload} />
            </DragFeedback>
          </FilesContainer>
          <HiddenFileInput
            ref={uploadRef}
            handleChange={handleUploadBtn}
          />
        </FormContainer>
      </Container>
    </DropArea>
  );
}

const Container = styled.div`
  padding: 40px;
  overflow: hidden;
  ${({ theme }) => css`
    background-color: ${theme.uploadPage.backgroundColor};
    border-radius: ${theme.uploadPage.containerRadius};
    margin: ${theme.uploadPage.margin};
  `}
`;

const DragFeedback = styled.div`
  z-index: 0;

  pointer-events: none;
  margin: auto;
  width: 400px;
  height: 400px;
  max-width: 75%;
  max-height: 75%;

  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;

  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;

  border-radius: 36px;
  transition: opacity .15s ease;
  opacity: .4;
  &.--dragging {
    opacity: .85;
  }

  ${({ theme }) => `
    ${theme.font(FontType.SemiBold, FontSize.Medium)};
    color: ${theme.colors.text};
    display: ${theme.uploadPage.uploadIconDisplay};
    border: 5px dashed ${theme.palette.grey300};
    ${theme.media.tablet}
    {
      display: none;
      ${theme.font(FontType.Strong, FontSize.Medium)};
    }
  `}
`;

const FormContainer = styled.form`
  display: ${({ theme }) => theme.uploadPage.formsDisplay};
  flex-direction: row;

  > * {
    margin-right: 12px;
  }

  ${({ theme }) => theme.media.mobile} {
    flex-direction: column;

    > * {
      margin-bottom: 12px;
      margin-right: 0;
    }
  }
`;

const FilesContainer = styled.div`
  display: flex;
  position: relative;
  min-height:  ${({ theme }) => theme.uploadPage.fileContainerSize};
  padding-bottom: 40px;
  flex-direction: row;
  flex-wrap: wrap;
  align-content: flex-start;
`;

const PlusIcon = styled(Plus)`
  ${({ theme }) => css`
    color:  ${theme.uploadButton.iconColor};
    height: ${theme.uploadButton.iconSize};
    width: ${theme.uploadButton.iconSize};
  `}
`;

const LargeUploadButton = styled(Button)`
  ${({ theme }) => css`
    height: ${theme.uploadButton.height};
    background-color: ${theme.uploadButton.backgroundColor};
    transform: translateY(0px);
    &:hover {
      background-color: ${theme.uploadButton.hover};
      transform: translateY(${theme.uploadButton.transform});
    }
    margin-left: ${theme.uploadButton.marginLeft};
    text-transform: ${theme.uploadButton.buttonCase};
    color: ${theme.uploadButton.color};
    ${theme.media.tablet}
    {
      width: 100%;
      max-width: 100%;
      margin-left: 0px;
      margin-right: 0px;
      margin-bottom: 50px;
      &:hover {
        transform: translateY(0px);
      }
    }
  `}
`;

export default observer(FileUploadView);
